<template>
  <div ref="containerRef" class="dg-wheel">
    <div class="dg-wheel__text-wrapper">
      <p class="dg-wheel__text">{{ selectedItem * 2 }} guides <br />/ month</p>
    </div>

    <div class="dg-wheel__options">
      <!-- Placeholder elements to always have an item in the center of the wheel picker -->
      <div
        v-for="n in 3"
        :key="-n"
        ref="optionRefs"
        class="dg-wheel__option dg-wheel__option--placeholder"
      >
        {{ -n }}
      </div>

      <div
        v-for="n in 99"
        :key="n"
        ref="optionRefs"
        class="dg-wheel__option"
        :class="{
          'dg-wheel__option--current': selectedItem === n,
        }"
      >
        {{ n }}
      </div>

      <!-- Placeholder elements to always have an item in the center of the wheel picker -->
      <div
        v-for="n in 3"
        :key="99 + n"
        ref="optionRefs"
        class="dg-wheel__option dg-wheel__option--placeholder"
      >
        {{ 99 + n }}
      </div>
    </div>

    <p class="dg-wheel__label">min</p>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'

import { PublicInterface } from '@/models/interfaces'
import { QuestionnaireView } from '@/models/onboarding'
import { DailyGoalChoice } from '@/models/onboarding.types'

const props = defineProps<{
  view: PublicInterface<QuestionnaireView>
}>()

const selectedItem = computed(() => {
  return props.view.currentChoice as DailyGoalChoice
})

// This is a Set instead of an array because Set ensures all items are unique.
const visibleItems = ref<Set<number>>(new Set())

const containerRef = ref<HTMLElement | null>(null)
const optionRefs = ref<HTMLElement[]>([])

const observer = ref<IntersectionObserver | null>(null)

onMounted(() => {
  // This observer adds items to `visibleItems` when they appear in the scroll wheel
  // and removes them when they disappear, so `visibleItems` always has the items
  // currently visible in the wheel.
  // After updating `visibleItems`, it assigns the value of the item in the middle
  // to `selectedItem`.
  observer.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const number = parseInt(entry.target.innerHTML)

        if (entry.isIntersecting) {
          visibleItems.value.add(number)
        } else {
          visibleItems.value.delete(number)
        }

        updateSelectedItem()
      })
    },
    { threshold: 0.5, root: containerRef.value },
  )

  optionRefs.value.forEach((el) => {
    observer.value?.observe(el)
  })

  // Scroll to the default value.
  optionRefs.value[selectedItem.value - 1]?.scrollIntoView()
})

onBeforeUnmount(() => {
  observer.value?.disconnect()
})

/**
 * Update `view.currentChoice` with the selected item (the item in the middle of the wheel).
 */
function updateSelectedItem(): void {
  const visibleItemsArray = Array.from(visibleItems.value)
  const sum = visibleItemsArray.reduce((sum, num) => sum + num, 0)

  // The item in the middle is the average of all other items.
  // The value is rounded up because at the top of the wheel
  // there are some negative values that break this logic.
  // For example: `[-1, -2, -3, 1, 2, 3, 4]`. The item in the
  // middle is `1`, but the average of the sum of this array is
  // `0.57`. If we round it up, we get `1`.
  props.view.setCurrentChoice(Math.ceil(sum / visibleItemsArray.length))
}
</script>

<style lang="scss" scoped>
$item-height: 36px;
$visible-items: 7;
$visible-unselected-items: ($visible-items - 1);

.dg-wheel {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-template-areas: 'left center right';
  align-items: center;

  &:before,
  &:after {
    content: '';
    grid-area: center;
    width: 100%;
    height: $item-height * $visible-unselected-items / 2;
    z-index: 1;
    pointer-events: none;
  }

  &:before {
    align-self: flex-start;
    background: linear-gradient(
      180deg,
      rgba($c-bg, 1) 25%,
      rgba($c-bg, 0.7) 100%
    );
  }

  &:after {
    align-self: flex-end;
    background: linear-gradient(
      0deg,
      rgba($c-bg, 1) 25%,
      rgba($c-bg, 0.7) 100%
    );
  }

  &__options {
    grid-area: center;
    max-height: $item-height * $visible-items;
    overflow-y: scroll;
    font-size: 32px;
    line-height: 36px;
    font-weight: 500;
    -ms-overflow-style: none;
    scrollbar-width: none;
    user-select: none;
    scroll-snap-type: y mandatory;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__option {
    color: $c-black-2;
    text-align: center;
    letter-spacing: 0.1em;
    scroll-snap-align: center;
  }

  &__option--placeholder {
    visibility: hidden;
  }

  &__text-wrapper {
    padding-block: 21px 19px;
    padding-inline: 16px 24px;
    margin-right: 8px;
    background: $c-border-7;
    max-width: 120px;
    clip-path: path(
      'M0 4C0 1.79086 1.79086 0 4 0H89.801C92.9625 0 95.8275 1.86187 97.1115 4.75089L109.556 32.7509C110.475 34.8194 110.475 37.1806 109.556 39.2491L97.1115 67.2491C95.8275 70.1381 92.9625 72 89.801 72H4C1.79086 72 0 70.2091 0 68V4Z'
    );
  }

  &__text {
    font-size: 14px;
    line-height: 16px;
    color: $c-black-15;
  }

  &__label {
    font-size: 18px;
    font-size: 20px;
    color: $c-black-15;
  }
}
</style>
